import React, { useEffect, useState, useContext } from "react";
import { initSchedule } from "../../../utils/data/initData";
import { AuthContext } from "../../../contexts/AuthContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import AllocationExpenseSchedule from "../../../components/Schedules/Others/AllocationExpenses/AllocationExpensesSchedule";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";

const AllocationExpensesSchedulePage = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);

  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Allocation_Of_Expenses",
      scheduleTitle: schedule.name,
      statutoryExpensesRows: scheduleDetails.statutoryExpensesRows,
      commonExpensesRows: scheduleDetails.commonExpensesRows,
      cappedRate: scheduleDetails.cappedRate,
      expenseCappedAmount: scheduleDetails.expenseCappedAmount,
      deductibleAllocationRows: scheduleDetails.deductibleAllocationRows,
      deductibleRowOffsets: scheduleDetails.deductibleRowOffsets ?? {},
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
      doc: scheduleDetails.doc,
    };

    setLoading(true);
    const response = await updatePLSchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        setLoading(false);
        return;
      }

      const response = await getScheduleData(
        scheduleId,
        "Allocation_Of_Expenses"
      );
      const scheduleData = response?.schedule;

      // init
      if (scheduleData) {
        setSchedule(initSchedule(scheduleData, "Allocation Of Expenses"));

        setScheduleDetails({
          dataRows: {
            incomeRows: scheduleData.incomeRows || [],
            statutoryExpensesRows: scheduleData.statutoryExpensesRows || [],
            commonExpensesRows: scheduleData.commonExpensesRows || [],
            cappedRate: scheduleData.cappedRate ?? 0,
            deductibleRowOffsets:
              scheduleData.deductibleRowOffsets &&
              typeof scheduleData.deductibleRowOffsets === "object"
                ? scheduleData.deductibleRowOffsets
                : {},
          },
          doc: scheduleData.doc,
        });
      }

      setLoading(false);
    };

    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, company?.uen]);

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <AllocationExpenseSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
        />
      )}
    </>
  );
};

export default AllocationExpensesSchedulePage;
